export interface AgentStatusData {
  agentId: number
  isOnline: boolean
  lastSeenAt: string
  activeSessions: number
}

export interface SessionData {
  sessionId: string
  status: string
  platform: string
  isHandedOver: boolean
  agentId?: number
  agentName?: string
  messageCount: number
  lastMessageAt?: string
}

export interface HandoverData {
  id: number
  sessionId: string
  requestedBy: string
  reason?: string
  priority: string
  status: string
  requestedAt: string
}

export interface NotificationData {
  id: string
  type: 'info' | 'warning' | 'error' | 'success'
  title: string
  message: string
  urgent?: boolean
  actionUrl?: string
  timestamp: string
}

export interface VisitorData {
  id: string
  sessionId?: string
  userId?: string
  timestamp: string
}

export interface WebSocketMessage {
  type: string
  data:
    | AgentStatusData
    | SessionData
    | HandoverData
    | NotificationData
    | VisitorData
    | Record<string, unknown>
  timestamp?: string
  id?: string
}

export interface WebSocketCallbacks {
  onMessage?: (message: WebSocketMessage) => void
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Event) => void
  onAgentStatusUpdate?: (data: AgentStatusData) => void
  onSessionUpdate?: (data: SessionData) => void
  onSessionAssigned?: (data: {
    sessionId: string
    agentId: number
    agentName: string
    timestamp: string
  }) => void
  onSessionTaken?: (data: { sessionId: string; agentId: number }) => void
  onHandoverRequest?: (data: HandoverData) => void
  onTransferRequestReceived?: (data: {
    handoverRequestId: number
    sessionId: string
    fromAgentId: number
    fromAgentName: string
    reason: string
    timestamp: string
  }) => void
  onTransferRequestSent?: (data: {
    handoverRequestId: number
    sessionId: string
    toAgentId: number
    reason: string
    timestamp: string
  }) => void
  onNotification?: (data: NotificationData) => void
  onVisitorConnected?: (data: VisitorData) => void
  onVisitorDisconnected?: (data: VisitorData) => void
}

export const WS_EVENTS = {
  // Connection events
  REGISTER: 'register',
  PING: 'ping',
  PONG: 'pong',
  CONNECTION_ESTABLISHED: 'connection_established',

  // Agent events
  AGENT_STATUS_UPDATE: 'agent_status_update',
  AGENT_ONLINE: 'agent_online',
  AGENT_OFFLINE: 'agent_offline',

  // Session events
  SESSION_CREATED: 'session_created',
  SESSION_UPDATED: 'session_updated',
  SESSION_ENDED: 'session_ended',
  SESSION_MESSAGE: 'session_message',
  SESSION_ASSIGNED: 'session_assigned',
  SESSION_TRANSFERRED: 'session_transferred',
  SESSION_TAKEN: 'session_taken',

  // Handover events
  HANDOVER_REQUESTED: 'handover_requested',
  HANDOVER_ASSIGNED: 'handover_assigned',
  HANDOVER_COMPLETED: 'handover_completed',

  // Notification events
  NOTIFICATION: 'notification',
  URGENT_NOTIFICATION: 'urgent_notification',

  // Stats events
  STATS_UPDATE: 'stats_update',

  // Ticket events
  TICKET_CREATED: 'ticket_created',
  TICKET_REPLY_CREATED: 'ticket_reply_created',
  TICKET_STATUS_UPDATED: 'ticket_status_updated',
  TICKET_ASSIGNED: 'ticket_assigned',
  TICKET_UPDATED: 'ticket_updated',
} as const

export type WebSocketEventType = (typeof WS_EVENTS)[keyof typeof WS_EVENTS]
